import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { Product } from '../data/products';

interface CartItem {
  product: Product;
  quantity: number;
}

interface CartState {
  items: CartItem[];
  itemCount: number;
  total: number;
}

type CartAction =
  | { type: 'ADD_ITEM'; payload: { product: Product; quantity: number } }
  | { type: 'REMOVE_ITEM'; payload: { productId: string } }
  | { type: 'UPDATE_QUANTITY'; payload: { productId: string; quantity: number } }
  | { type: 'CLEAR_CART' };

interface CartContextType {
  cart: CartState;
  addItem: (product: Product, quantity: number) => void;
  removeItem: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
}

const defaultCartState: CartState = {
  items: [],
  itemCount: 0,
  total: 0,
};

const CartContext = createContext<CartContextType | undefined>(undefined);

const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'ADD_ITEM': {
      const { product, quantity } = action.payload;
      const existingItemIndex = state.items.findIndex((item) => item.product.id === product.id);

      if (existingItemIndex > -1) {
        // Item exists, update quantity
        const updatedItems = [...state.items];
        updatedItems[existingItemIndex].quantity += quantity;

        return {
          ...state,
          items: updatedItems,
          itemCount: state.itemCount + quantity,
          total: state.total + (product.discountPrice || product.price) * quantity,
        };
      } else {
        // Add new item
        return {
          ...state,
          items: [...state.items, { product, quantity }],
          itemCount: state.itemCount + quantity,
          total: state.total + (product.discountPrice || product.price) * quantity,
        };
      }
    }
    case 'REMOVE_ITEM': {
      const { productId } = action.payload;
      const itemToRemove = state.items.find((item) => item.product.id === productId);
      
      if (!itemToRemove) return state;

      const updatedItems = state.items.filter((item) => item.product.id !== productId);
      
      return {
        ...state,
        items: updatedItems,
        itemCount: state.itemCount - itemToRemove.quantity,
        total: state.total - (itemToRemove.product.discountPrice || itemToRemove.product.price) * itemToRemove.quantity,
      };
    }
    case 'UPDATE_QUANTITY': {
      const { productId, quantity } = action.payload;
      const itemIndex = state.items.findIndex((item) => item.product.id === productId);
      
      if (itemIndex === -1) return state;
      
      const updatedItems = [...state.items];
      const oldQuantity = updatedItems[itemIndex].quantity;
      updatedItems[itemIndex].quantity = quantity;
      
      const itemPrice = updatedItems[itemIndex].product.discountPrice || updatedItems[itemIndex].product.price;
      const priceDifference = itemPrice * (quantity - oldQuantity);
      
      return {
        ...state,
        items: updatedItems,
        itemCount: state.itemCount + (quantity - oldQuantity),
        total: state.total + priceDifference,
      };
    }
    case 'CLEAR_CART':
      return defaultCartState;
    default:
      return state;
  }
};

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [cart, dispatch] = useReducer(cartReducer, defaultCartState);

  const addItem = (product: Product, quantity: number) => {
    dispatch({ type: 'ADD_ITEM', payload: { product, quantity } });
  };

  const removeItem = (productId: string) => {
    dispatch({ type: 'REMOVE_ITEM', payload: { productId } });
  };

  const updateQuantity = (productId: string, quantity: number) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { productId, quantity } });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};